import { formatNumber } from "sipli-fleet-core/utils/formatNumber";
import { cn } from "./cn";
import { getFromLocal } from "./cache.service";
import { LOCAL_KEYS } from "./util.constant";

export { cn };
export const formatDate = (date: Date | string | number, format = "DD/MM/YYYY"): string => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return format
    .replace("DD", day)
    .replace("MM", month)
    .replace("YYYY", String(year));
};

export const debounce = <T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout | null = null;

  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
};

export const throttle = <T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;

  return function executedFunction(...args: Parameters<T>) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};

export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const truncate = (str: string, length: number): string => {
  return str.length > length ? `${str.substring(0, length)}...` : str;
};

export const getUserShortName = (): string => {
  const user = getFromLocal<{ name?: string }>(LOCAL_KEYS.USER_DETAIL, true);
  if (!user?.name) return "MA";

  const names = user.name.split(" ");
  if (names.length >= 2) {
    const first = names[0]?.[0];
    const second = names[1]?.[0];
    if (first && second) {
      return first.toUpperCase() + second.toUpperCase();
    }
  }
  return user.name.substring(0, 2).toUpperCase();
};

export const getUserDisplayName = (): string => {
  const user = getFromLocal<{ name?: string }>(LOCAL_KEYS.USER_DETAIL, true);
  return user?.name || "My Account";
};

type DateLike = unknown;

const toDate = (value: DateLike | null | undefined) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") return new Date(value);
  if (
    typeof value === "object" &&
    "format" in value &&
    typeof value.format === "function"
  ) {
    return new Date(value.format("YYYY-MM-DD HH:mm:ss"));
  }
  return null;
};

const formatDateTime = (value: DateLike | null | undefined, time = "00:00:00") => {
  const date = toDate(value);
  if (!date || Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day} ${time}`;
};

export const UtilService = {
  disabledDate(current: DateLike, startDate?: DateLike, endDate?: DateLike) {
    const currentDate = toDate(current);
    const minDate = toDate(startDate);
    const maxDate = toDate(endDate);

    if (!currentDate) return false;
    if (minDate && currentDate < minDate) return true;
    if (maxDate && currentDate > maxDate) return true;
    return false;
  },

  globalConvertDateValues(values: unknown[]) {
    return values;
  },

  dateRangePickerFormatedDate(value: DateLike | null | undefined, time = "00:00:00") {
    return formatDateTime(value, time);
  },

  downloadCSV(data: unknown, filename = "download.csv") {
    const content = typeof data === "string" ? data : JSON.stringify(data ?? "");
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  },

  downloadBinaryPdf(data: BlobPart, filename = "download.pdf") {
    const blob = new Blob([data], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  },

  textLimit(text: string, limit = 30) {
    return text.length > limit ? `${text.slice(0, limit)}...` : text;
  },
};

/** Formats dynamic measurements, preserving identifiers and coded values as supplied. */
export const formatFieldValue = (value: unknown, field: string, language = "en"): string => {
  if (value === null || value === undefined || value === "") return "--";
  if (typeof value === "object") return JSON.stringify(value);

  const text = String(value);
  const name = field
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9/]+/g, " ");
  // Check field meaning before numeric conversion to preserve leading zeros and long IDs.
  const identifier = /\b(?:ids?|identifier|identification|imei\d*|imsi\d*|iccid\d*|ccid\d*|vin|serial|s\/n|uuid|guid|code|pin|phone|mobile|msisdn|ip|mac|plate|targa|matricola|serie|seriennummer|chassis|frame|timestamp|epoch|date|time|packet|payload|hex|binary|bitmask|name|label|type|format|source|operator|forward)\b/;
  if (identifier.test(name)) return text;

  if (typeof value === "number") return formatNumber(value, language);
  if (typeof value !== "string" || !/^[+-]?\d+(?:[.,]\d+)?$/.test(value.trim())) return text;
  return formatNumber(value, language);
};
